import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, GermanDecimalInput, SelectInput, TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  erstelleMahnungsEntwuerfe,
  formatMahnungDate,
  listMahnungen,
  loadMahnlauf,
  mahnungStatusLabel,
  setMahnungPause,
  setzeZahlungsfrist,
  versandartLabel,
  type MahnlaufDaten,
  type MahnlaufFilter,
  type MahnlaufItem,
  type MahnungListItem,
  type MahnungStatus,
} from "@/lib/mahnwesen";
import { notifyMahnwesenBadgeRefresh } from "@/lib/navBadges";
import { toast } from "@/lib/toast";
import { MahnwesenEinstellungenTab } from "@/pages/dokumente/MahnwesenEinstellungen";
import { ZahlungsfristDialog } from "@/pages/dokumente/mahnung/ZahlungsfristDialog";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";

const FILTER_OPTIONS: Array<{ value: MahnlaufFilter; label: string }> = [
  { value: "mahnbar", label: "Jetzt mahnbar" },
  { value: "ueberfaellig", label: "Überfällig" },
  { value: "alle", label: "Alle offenen" },
  { value: "gesperrt", label: "Gesperrt / pausiert" },
];

type Tab = "mahnlauf" | "vorgaenge" | "einstellungen";
const TABS: Tab[] = ["mahnlauf", "vorgaenge", "einstellungen"];

const STATUS_FILTER: Array<{ value: MahnungStatus | ""; label: string }> = [
  { value: "", label: "Alle" },
  { value: "entwurf", label: "Entwürfe (noch zu prüfen)" },
  { value: "erstellt", label: "Finalisiert – noch zu versenden" },
  { value: "fehlgeschlagen", label: "Versand fehlgeschlagen" },
  { value: "versendet", label: "Versendet" },
  { value: "storniert", label: "Storniert" },
];

/** Kurzanleitung über dem Mahnlauf – die drei Schritte bis zur versendeten Mahnung. */
function AblaufHinweis() {
  const schritte = [
    { titel: "Rechnungen auswählen", text: "Unten die überfälligen Rechnungen anhaken und „Mahnentwürfe erstellen“." },
    { titel: "Entwurf prüfen", text: "Empfänger, Texte und Vorschau kontrollieren – dann „Finalisieren“." },
    { titel: "Versenden", text: "Per E-Mail direkt aus der App oder ausdrucken und Versand dokumentieren." },
  ];
  return (
    <ol className="grid gap-2 rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 sm:grid-cols-3">
      {schritte.map((s, idx) => (
        <li key={s.titel} className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
            {idx + 1}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-label">{s.titel}</span>
            <span className="block text-xs text-label-secondary">{s.text}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "orange" | "red";
}) {
  return (
    <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
      <div className="text-xs font-medium uppercase tracking-wide text-label-secondary">{label}</div>
      <div
        className={
          "mt-1 text-xl font-semibold tabular-nums " +
          (tone === "orange" ? "text-status-orange" : tone === "red" ? "text-status-red" : "text-label")
        }
      >
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-xs text-label-tertiary">{hint}</div> : null}
    </div>
  );
}

function StatusBadge({ status }: { status: MahnungStatus }) {
  const cls =
    status === "entwurf"
      ? "bg-fill-tertiary text-label-secondary"
      : status === "erstellt"
        ? "bg-status-orange/15 text-status-orange"
        : status === "versendet"
          ? "bg-status-green/15 text-status-green"
          : "bg-status-red/15 text-status-red";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {mahnungStatusLabel(status, true)}
    </span>
  );
}

export function MahnwesenPage() {
  const navigate = useNavigate();
  const { open: openDocumentEditor } = useDocumentEditor();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : "mahnlauf";
  const setTab = useCallback(
    (next: Tab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "mahnlauf") params.delete("tab");
          else params.set("tab", next);
          if (next !== "einstellungen") params.delete("stufe");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // --- Mahnlauf-State
  const [filter, setFilter] = useState<MahnlaufFilter>("mahnbar");
  const [q, setQ] = useState("");
  const [stufeFilter, setStufeFilter] = useState("");
  const [minBetrag, setMinBetrag] = useState(0);
  const [daten, setDaten] = useState<MahnlaufDaten | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sammeln, setSammeln] = useState(true);
  const [stufeOverride, setStufeOverride] = useState("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [fristItem, setFristItem] = useState<MahnlaufItem | null>(null);
  const [fristIntent, setFristIntent] = useState<"frist" | "fortsetzen">("frist");

  // --- Vorgänge-State
  const [vorgaenge, setVorgaenge] = useState<MahnungListItem[]>([]);
  const [vorgaengeLoading, setVorgaengeLoading] = useState(false);
  const [vorgangStatus, setVorgangStatus] = useState("");
  const [vorgangQ, setVorgangQ] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadMahnlauf({
        filter,
        q: q.trim() || undefined,
        stufe: stufeFilter || undefined,
        min_betrag: minBetrag > 0 ? minBetrag : undefined,
      });
      setDaten(result);
      setSelected((prev) => {
        const gueltig = new Set(result.items.map((i) => i.dokument_id));
        return new Set([...prev].filter((id) => gueltig.has(id)));
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mahnwesen konnte nicht geladen werden.");
      setDaten(null);
    } finally {
      setLoading(false);
    }
  }, [filter, q, stufeFilter, minBetrag]);

  const reloadVorgaenge = useCallback(async () => {
    setVorgaengeLoading(true);
    try {
      setVorgaenge(
        await listMahnungen({
          status: vorgangStatus || undefined,
          q: vorgangQ.trim() || undefined,
          limit: 300,
        }),
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Mahnungen konnten nicht geladen werden.");
    } finally {
      setVorgaengeLoading(false);
    }
  }, [vorgangStatus, vorgangQ]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (tab === "vorgaenge") void reloadVorgaenge();
  }, [tab, reloadVorgaenge]);

  const items = daten?.items || [];
  const kpis = daten?.kpis;
  const stufen = daten?.stufen || [];
  const permissions = daten?.permissions;
  const basiszins = daten?.basiszins;

  const auswaehlbar = useMemo(
    () => items.filter((i) => i.naechste_stufe_code && !i.sperre_aktiv && !i.pausiert),
    [items],
  );
  const alleAusgewaehlt = auswaehlbar.length > 0 && auswaehlbar.every((i) => selected.has(i.dokument_id));

  function toggleAll() {
    setSelected(alleAusgewaehlt ? new Set() : new Set(auswaehlbar.map((i) => i.dokument_id)));
  }

  function toggleOne(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleEntwuerfeErstellen() {
    if (!selected.size) return;
    setCreating(true);
    try {
      const result = await erstelleMahnungsEntwuerfe({
        dokument_ids: [...selected],
        stufe_code: stufeOverride || undefined,
        sammeln,
      });
      const anzahl = result.entwuerfe.length;
      toast.success(
        anzahl === 1 ? "1 Mahnentwurf erstellt." : `${anzahl} Mahnentwürfe erstellt.`,
      );
      setSelected(new Set());
      notifyMahnwesenBadgeRefresh();
      const einziger = anzahl === 1 ? result.entwuerfe[0] : undefined;
      if (einziger) {
        navigate(`/dokumente/mahnwesen/${einziger.id}`);
      } else {
        setTab("vorgaenge");
        setVorgangStatus("entwurf");
        await reload();
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Entwürfe konnten nicht erstellt werden.");
    } finally {
      setCreating(false);
    }
  }

  async function handlePause(item: MahnlaufItem, pausiert: boolean) {
    setBusyId(item.dokument_id);
    try {
      await setMahnungPause(item.dokument_id, pausiert);
      toast.success(pausiert ? "Mahnung pausiert." : "Mahnung fortgesetzt.");
      notifyMahnwesenBadgeRefresh();
      setFristItem(null);
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  function openFrist(item: MahnlaufItem, intent: "frist" | "fortsetzen") {
    setFristIntent(intent);
    setFristItem(item);
  }

  async function handleFrist(body: { faelligkeitsdatum: string; pause_aufheben: boolean }) {
    if (!fristItem) return;
    setBusyId(fristItem.dokument_id);
    try {
      await setzeZahlungsfrist(fristItem.dokument_id, body);
      toast.success(
        body.pause_aufheben
          ? `Fällig am ${formatMahnungDate(body.faelligkeitsdatum)}, Mahnung fortgesetzt.`
          : `Fällig am ${formatMahnungDate(body.faelligkeitsdatum)} gesetzt.`,
      );
      notifyMahnwesenBadgeRefresh();
      setFristItem(null);
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Zahlungsfrist konnte nicht gesetzt werden.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell title="Mahnwesen">
      <div className="space-y-4 md:space-y-5">
        {kpis ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard
              label="Offen gesamt"
              value={formatCurrency(kpis.offen_gesamt, "EUR")}
              hint={`${kpis.anzahl_offen} Rechnungen`}
            />
            <KpiCard
              label="Überfällig"
              value={formatCurrency(kpis.ueberfaellig_gesamt, "EUR")}
              hint={`${kpis.anzahl_ueberfaellig} Rechnungen · Ø ${kpis.durchschnitt_ueberfaellig_tage} Tage`}
              tone="orange"
            />
            <KpiCard label="Heute mahnbar" value={String(kpis.heute_mahnbar)} tone={kpis.heute_mahnbar > 0 ? "red" : undefined} />
            <KpiCard label="Offene Entwürfe" value={String(kpis.offene_entwuerfe)} />
            <KpiCard label="Gesperrt / pausiert" value={String(kpis.mit_sperre)} />
            <KpiCard label="Zahlungsvereinbarungen" value={String(kpis.mit_vereinbarung)} />
          </div>
        ) : null}

        {basiszins && !basiszins.aktuell_gueltig && tab !== "einstellungen" ? (
          <Alert variant="warning" title="Basiszinssatz nicht aktuell">
            Für den aktuellen Zeitraum ist kein Basiszinssatz (§ 247 BGB) gepflegt
            {basiszins.gepflegt_bis ? ` (gepflegt bis ${formatMahnungDate(basiszins.gepflegt_bis)})` : ""}.
            Verzugszinsen können für neue Zeiträume nicht berechnet werden.{" "}
            <button
              type="button"
              className="font-medium underline"
              onClick={() => setTab("einstellungen")}
            >
              Jetzt in den Einstellungen ergänzen
            </button>
          </Alert>
        ) : null}

        <SegmentedControl<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: "mahnlauf", label: "Mahnlauf" },
            { value: "vorgaenge", label: "Mahnungen" },
            { value: "einstellungen", label: "Einstellungen" },
          ]}
        />

        {tab === "mahnlauf" ? (
          <>
            <AblaufHinweis />
            {kpis && kpis.offene_entwuerfe > 0 ? (
              <Alert variant="info">
                {kpis.offene_entwuerfe === 1
                  ? "Es liegt 1 Entwurf, der noch geprüft und finalisiert werden muss."
                  : `Es liegen ${kpis.offene_entwuerfe} Entwürfe, die noch geprüft und finalisiert werden müssen.`}{" "}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => {
                    setVorgangStatus("entwurf");
                    setTab("vorgaenge");
                  }}
                >
                  Entwürfe anzeigen
                </button>
              </Alert>
            ) : null}
            <section data-tour="mahnung-liste" className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0 space-y-1">
                  <h2 className="text-lg font-semibold text-label md:text-xl">Schritt 1 · Offene Forderungen</h2>
                  <p className="text-sm text-label-secondary">
                    Rechnungen anhaken und Mahnentwürfe erstellen. Nichts wird sofort verschickt – jeder Entwurf wird
                    erst geprüft, finalisiert und dann versendet.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-end">
                  <div className="w-full sm:w-44">
                    <SelectInput label="Filter" value={filter} onChange={(e) => setFilter(e.target.value as MahnlaufFilter)}>
                      {FILTER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </SelectInput>
                  </div>
                  <div className="w-full sm:w-44">
                    <SelectInput label="Aktuelle Stufe" value={stufeFilter} onChange={(e) => setStufeFilter(e.target.value)}>
                      <option value="">Alle Stufen</option>
                      <option value="keine">Noch nicht gemahnt</option>
                      {stufen.map((s) => (
                        <option key={s.id} value={s.code}>
                          {s.bezeichnung}
                        </option>
                      ))}
                    </SelectInput>
                  </div>
                  <div className="w-full sm:w-36">
                    <GermanDecimalInput
                      label="Min. Betrag"
                      value={minBetrag}
                      onValueChange={setMinBetrag}
                      emptyWhenZero
                    />
                  </div>
                  <div className="w-full sm:w-56">
                    <TextInput
                      label="Suche"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Kunde, Rechnungsnr. …"
                    />
                  </div>
                </div>
              </div>

              {selected.size > 0 ? (
                <div className="mt-4 flex flex-col gap-3 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="text-sm font-medium text-label">
                    {selected.size} {selected.size === 1 ? "Rechnung" : "Rechnungen"} ausgewählt
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="w-full sm:w-52">
                      <SelectInput
                        label="Mahnstufe"
                        hint="Leer = automatischer Vorschlag"
                        value={stufeOverride}
                        onChange={(e) => setStufeOverride(e.target.value)}
                      >
                        <option value="">Automatisch (nächste Stufe)</option>
                        {stufen
                          .filter((s) => s.aktiv)
                          .map((s) => (
                            <option key={s.id} value={s.code}>
                              {s.bezeichnung}
                            </option>
                          ))}
                      </SelectInput>
                    </div>
                    <Checkbox
                      label="Rechnungen je Kunde bündeln (Sammelmahnung)"
                      checked={sammeln}
                      onChange={(e) => setSammeln(e.target.checked)}
                      className="pb-2"
                    />
                    <Button
                      type="button"
                      data-tour="mahnung-entwurf"
                      loading={creating}
                      disabled={!permissions?.erstellen}
                      onClick={() => void handleEntwuerfeErstellen()}
                    >
                      Mahnentwürfe erstellen
                    </Button>
                  </div>
                </div>
              ) : null}
            </section>

            {error ? <Alert variant="error">{error}</Alert> : null}

            <section className="overflow-hidden rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
              {loading ? (
                <div className="flex items-center gap-2 px-4 py-10 text-sm text-label-secondary">
                  <Spinner />
                  Wird geladen …
                </div>
              ) : items.length === 0 ? (
                <p className="px-4 py-10 text-sm text-label-secondary">Keine Einträge für diesen Filter.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-separator bg-fill-tertiary/40 text-xs uppercase tracking-wide text-label-secondary">
                      <tr>
                        <th className="w-10 px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={alleAusgewaehlt}
                            onChange={toggleAll}
                            aria-label="Alle auswählen"
                          />
                        </th>
                        <th className="px-3 py-2.5 font-semibold">Kunde / Rechnung</th>
                        <th className="px-3 py-2.5 font-semibold">Fällig</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Offen</th>
                        <th className="px-3 py-2.5 font-semibold">Stufe</th>
                        <th className="px-3 py-2.5 font-semibold">Nächste Stufe</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const selectable = Boolean(item.naechste_stufe_code) && !item.sperre_aktiv && !item.pausiert;
                        return (
                          <tr key={item.dokument_id} className="border-b border-separator/70 last:border-0">
                            <td className="px-3 py-3 align-top">
                              <input
                                type="checkbox"
                                checked={selected.has(item.dokument_id)}
                                disabled={!selectable}
                                onChange={() => toggleOne(item.dokument_id)}
                                aria-label={`Rechnung ${item.dokument_nummer || item.dokument_id} auswählen`}
                              />
                            </td>
                            <td className="px-3 py-3 align-top">
                              <button
                                type="button"
                                className="text-left font-medium text-accent hover:underline"
                                onClick={() => openDocumentEditor({ docId: item.dokument_id })}
                              >
                                {item.dokument_nummer || `#${item.dokument_id}`}
                              </button>
                              <div className="text-xs text-label-secondary">{item.kunde_name || "—"}</div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {item.kundenart ? (
                                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                                    {item.kundenart === "unternehmen" ? "B2B" : "B2C"}
                                  </span>
                                ) : null}
                                {item.sperre_aktiv ? (
                                  <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-[11px] font-medium text-status-red">
                                    {item.sperre_status === "inkasso"
                                      ? "Inkasso"
                                      : item.sperre_status === "gerichtlich"
                                        ? "Gerichtlich"
                                        : "Mahnsperre"}
                                  </span>
                                ) : null}
                                {item.pausiert ? (
                                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                                    Pausiert
                                  </span>
                                ) : null}
                                {item.vereinbarung_aktiv ? (
                                  <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-[11px] font-medium text-status-orange">
                                    Zahlungsvereinbarung
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top tabular-nums">
                              <div>{formatMahnungDate(item.faelligkeitsdatum)}</div>
                              {item.tage_ueberfaellig > 0 ? (
                                <div className="text-xs text-status-orange">{item.tage_ueberfaellig} Tage überfällig</div>
                              ) : null}
                            </td>
                            <td className="px-3 py-3 align-top text-right tabular-nums">
                              <div className="font-medium">{formatCurrency(item.summe_offen, "EUR")}</div>
                              {item.summe_bezahlt > 0 ? (
                                <div className="text-xs text-label-secondary">
                                  bezahlt {formatCurrency(item.summe_bezahlt, "EUR")}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium">
                                {item.aktuelle_stufe_label}
                              </span>
                              {item.letzte_mahnung_am ? (
                                <div className="mt-1 text-[11px] text-label-tertiary">
                                  am {formatMahnungDate(item.letzte_mahnung_am)}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-3 align-top text-xs">
                              {item.naechste_stufe_label ? (
                                <>
                                  <div className="font-medium text-label">{item.naechste_stufe_label}</div>
                                  <div className="text-label-secondary">ab {formatMahnungDate(item.empfohlen_am)}</div>
                                  {item.mahnbar ? (
                                    <div className="mt-0.5 font-semibold text-status-orange">Jetzt fällig</div>
                                  ) : null}
                                </>
                              ) : (
                                <span className="text-label-secondary">Höchste Stufe erreicht</span>
                              )}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex flex-wrap justify-end gap-1.5">
                                {item.entwurf_ids.length ? (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => navigate(`/dokumente/mahnwesen/${item.entwurf_ids[0]}`)}
                                  >
                                    Entwurf öffnen
                                  </Button>
                                ) : null}
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  disabled={!permissions?.sperren_verwalten || busyId === item.dokument_id}
                                  onClick={() =>
                                    item.pausiert
                                      ? openFrist(item, "fortsetzen")
                                      : void handlePause(item, true)
                                  }
                                >
                                  {item.pausiert ? "Fortsetzen" : "Pause"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  disabled={!permissions?.sperren_verwalten || busyId === item.dokument_id}
                                  onClick={() => openFrist(item, "frist")}
                                >
                                  Frist
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : tab === "vorgaenge" ? (
          <>
            <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
              <div className="mb-3 space-y-1">
                <h2 className="text-lg font-semibold text-label md:text-xl">Zahlungserinnerungen &amp; Mahnungen</h2>
                <p className="text-sm text-label-secondary">
                  Alle Vorgänge – vom Entwurf bis zur versendeten Mahnung. Zum Öffnen auf eine Zeile klicken.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="w-full sm:w-64">
                  <SelectInput label="Status" value={vorgangStatus} onChange={(e) => setVorgangStatus(e.target.value)}>
                    {STATUS_FILTER.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div className="w-full sm:w-64">
                  <TextInput
                    label="Suche"
                    value={vorgangQ}
                    onChange={(e) => setVorgangQ(e.target.value)}
                    placeholder="Nummer, Kunde, Rechnung …"
                  />
                </div>
              </div>
            </section>

            <section className="overflow-hidden rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
              {vorgaengeLoading ? (
                <div className="flex items-center gap-2 px-4 py-10 text-sm text-label-secondary">
                  <Spinner />
                  Wird geladen …
                </div>
              ) : vorgaenge.length === 0 ? (
                <p className="px-4 py-10 text-sm text-label-secondary">Keine Mahnungen gefunden.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-separator bg-fill-tertiary/40 text-xs uppercase tracking-wide text-label-secondary">
                      <tr>
                        <th className="px-3 py-2.5 font-semibold">Nummer</th>
                        <th className="px-3 py-2.5 font-semibold">Stufe</th>
                        <th className="px-3 py-2.5 font-semibold">Kunde</th>
                        <th className="px-3 py-2.5 font-semibold">Rechnungen</th>
                        <th className="px-3 py-2.5 font-semibold">Datum</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Gesamt</th>
                        <th className="px-3 py-2.5 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vorgaenge.map((m) => (
                        <tr
                          key={m.id}
                          className="cursor-pointer border-b border-separator/70 last:border-0 hover:bg-fill-tertiary/30"
                          onClick={() => navigate(`/dokumente/mahnwesen/${m.id}`)}
                        >
                          <td className="px-3 py-3 align-top font-medium text-accent">
                            {m.nummer || `Entwurf #${m.id}`}
                            {m.legacy ? (
                              <span className="ml-2 rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                                Altsystem
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-3 align-top">{m.stufe_bezeichnung}</td>
                          <td className="px-3 py-3 align-top">{m.kunde_name || "—"}</td>
                          <td className="px-3 py-3 align-top text-xs text-label-secondary">
                            {m.rechnungsnummern || "—"}
                          </td>
                          <td className="px-3 py-3 align-top tabular-nums">{formatMahnungDate(m.belegdatum)}</td>
                          <td className="px-3 py-3 align-top text-right font-medium tabular-nums">
                            {formatCurrency(m.summe_gesamt, "EUR")}
                          </td>
                          <td className="px-3 py-3 align-top">
                            <StatusBadge status={m.status} />
                            {m.versendet_am ? (
                              <div className="mt-1 text-[11px] text-label-tertiary">
                                {formatMahnungDate(m.versendet_am)}
                                {m.versendet_via ? ` · ${versandartLabel(m.versendet_via)}` : ""}
                              </div>
                            ) : m.status === "erstellt" ? (
                              <div className="mt-1 text-[11px] text-status-orange">Versand steht aus</div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : (
          <MahnwesenEinstellungenTab />
        )}
      </div>
      <ZahlungsfristDialog
        open={fristItem != null}
        onClose={() => setFristItem(null)}
        dokumentNummer={fristItem?.dokument_nummer ?? null}
        kundeName={fristItem?.kunde_name ?? null}
        aktuelleFaelligkeit={fristItem?.faelligkeitsdatum ?? null}
        pausiert={Boolean(fristItem?.pausiert)}
        intent={fristIntent}
        busy={fristItem != null && busyId === fristItem.dokument_id}
        onSubmit={(body) => void handleFrist(body)}
        onFortsetzenOhneFrist={
          fristItem ? () => void handlePause(fristItem, false) : undefined
        }
      />
    </AppShell>
  );
}
